import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders } from '../config/api';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

const ProfileCompletionProgress = () => {
 const [completionData, setCompletionData] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetchCompletionStatus();
 }, []);

 const fetchCompletionStatus = async () => {
 try {
 const token = localStorage.getItem('token') || localStorage.getItem('access_token');
 if (!token) {
 setLoading(false);
 return;
 }

 const response = await fetch(`${API_BASE_URL}/profile/completion-status`, {
 headers: getAuthHeaders(token)
 });

 if (response.ok) {
 const data = await response.json();
 setCompletionData(data);
 }
 } catch (error) {
 console.error('Error fetching completion status:', error);
 } finally {
 setLoading(false);
 }
 };

  if (loading) {
    return (
      <div className="card animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-5 bg-slate-200 rounded-lg w-1/3"></div>
          <div className="h-7 bg-slate-200 rounded-lg w-12"></div>
        </div>
        <div className="h-2.5 bg-slate-200 rounded-full mb-4"></div>
        <div className="space-y-2">
          <div className="h-3.5 bg-slate-200 rounded-lg w-1/2"></div>
          <div className="h-3.5 bg-slate-200 rounded-lg w-2/3"></div>
        </div>
      </div>
    );
  }

  // Don't show the widget if profile data is not available or is 100% complete
  if (!completionData || completionData.percentage === 100) return null;

  const { percentage, completed_sections, total_sections, missing_sections, is_complete } = completionData;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-bold text-slate-800 tracking-tight">Profile Completion</h3>
          <p className="text-xs text-slate-500 font-medium">
            {completed_sections} of {total_sections} sections completed
          </p>
        </div>
        <span className="text-2xl font-black text-primary-600 tracking-tight">{percentage}%</span>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-slate-100 border border-slate-200/80 rounded-full h-2.5 mb-4 overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-500 ${
            percentage === 100 
              ? 'bg-success-500' 
              : 'bg-gradient-to-r from-primary-600 to-indigo-600'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Status Message */}
      {is_complete ? (
        <div className="flex items-center text-success-700 bg-success-50 border border-success-200 rounded-xl p-3.5 mb-4 text-xs font-semibold">
          <CheckCircleIcon className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>Profile saved. You can now apply directly to universities.</span>
        </div>
      ) : (
        <>
          <div className="bg-warning-50 border border-warning-200 rounded-xl p-3.5 mb-4">
            <div className="flex items-start gap-2.5">
              <ExclamationCircleIcon className="w-4 h-4 text-warning-600 shrink-0 mt-0.5" />
              <p className="text-xs text-warning-800 font-medium leading-relaxed">
                <span className="font-bold text-warning-900">Get Personalized Recommendations:</span> Add your intermediate marks in Settings to receive tailored university recommendations.
              </p>
            </div>
          </div>
        </>
      )}

      {/* Missing Sections */}
      {missing_sections && missing_sections.length > 0 && (
        <div className="mb-5">
          <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Remaining Sections</h4>
          <div className="flex flex-wrap gap-2">
            {missing_sections.map((section, index) => (
              <span key={index} className="inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 border border-slate-200">
                <span className="w-1.5 h-1.5 bg-danger-500 rounded-full mr-1.5 shrink-0" />
                {section}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Action Button */}
      <div className="pt-1">
        <Link
          to="/application-form"
          className="btn-primary text-sm inline-flex items-center justify-center py-2.5"
        >
          Complete Student Profile
        </Link>
      </div>
    </div>
  );
};

export default ProfileCompletionProgress;